import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import Layout from "./components/Layout";

import Dashboard from "./pages/Dashboard";
import EmployeeDirectory from "./pages/EmployeeDirectory";
import AIWishes from "./pages/AIWishes";
import AIGifting from "./pages/AIGifting";
import SelectFestival from "./pages/SelectFestival";
import GreetingCard from "./pages/GreetingCard";
import FestivalCalendar from "./pages/FestivalCalendar";
import WishesBadges from "./pages/WishesBadges";

import AdminBranding from "./pages/AdminBranding";
import AdminPeople from "./pages/AdminPeople";
import Memories from "./pages/Memories";
import AdminOrgUnits from "./pages/AdminOrgUnits";
import AdminTranscriptIngest from "./pages/AdminTranscriptIngest";

import Landing from "./pages/Landing";
import AboutPublic from "./pages/AboutPublic";
import ContactPublic from "./pages/ContactPublic";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import AcceptInvite from "./pages/AcceptInvite";
import AdminMemories from "./pages/AdminMemories";

import { useAuth } from "./context/AuthContext";

import "./index.css";

/* -------------------------------
   Route Guards
-------------------------------- */

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified === false) {
    const qs = new URLSearchParams({
      verify: "required",
      email: user.email ? String(user.email) : "",
    });
    return <Navigate to={`/login?${qs.toString()}`} replace />;
  }

  return children;
};

const RequireRole = ({ allowed, children }) => {
  const { user } = useAuth();

  if (!user || !allowed.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/* -------------------------------
   App
-------------------------------- */

export default function App() {
  const [darkMode, setDarkMode] = useState(localStorage.getItem("darkMode") === "true");

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    localStorage.setItem("darkMode", darkMode.toString());
  }, [darkMode]);

  return (
    <Router>
      <Routes>
        {/* ---------- Public ---------- */}
        <Route path="/" element={<Landing />} />
        <Route path="/about" element={<AboutPublic />} />
        <Route path="/contact" element={<ContactPublic />} />

        {/* ---------- Auth ---------- */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* ---------- Protected ---------- */}
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <Dashboard />
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/employees"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <EmployeeDirectory />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/ai-wishes"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <AIWishes />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />
        <Route
          path="/gifting"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <AIGifting />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/select-festival"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <SelectFestival />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/greeting-card/:festival"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <GreetingCard />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/calendar"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <FestivalCalendar />
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/memories"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <Memories />
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/wishes-badges"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <WishesBadges />
              </Layout>
            </RequireAuth>
          }
        />

        {/* ---------- Admin ---------- */}
        <Route
          path="/admin/people"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <AdminPeople />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/admin/branding"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <AdminBranding />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/memories"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <AdminMemories />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/org-units"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <RequireRole allowed={["company_admin"]}>
                  <AdminOrgUnits />
                </RequireRole>
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/transcript-ingests"
          element={
            <RequireAuth>
              <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                <AdminTranscriptIngest />
              </Layout>
            </RequireAuth>
          }
        />

        <Route
          path="/admin/transcript-ingests"
          element={<Navigate to="/transcript-ingests" replace />}
        />


        {/* ---------- Redirects ---------- */}
        <Route path="/app" element={<Navigate to="/dashboard" replace />} />

        {/* ---------- Fallback ---------- */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}
